import { RepeatableListInput } from './RepeatableListInput';
import { FileUploadZone, type UploadedFile } from './FileUploadZone';

interface SalesTimelineStage {
  stage: string;
  order: number;
}

interface SalesData {
  targetBuyers: string;
  topBuyerTitles: string[];
  buyerChannels: string;
  salesProcessTimeline: SalesTimelineStage[];
  salesCycle: string;
  majorObjections: string[];
  objectionHandling: string;
}

interface SalesSectionProps {
  data: SalesData;
  onChange: (data: SalesData) => void;
  questionnaireId: string;
  uploads: UploadedFile[];
  onUploadsChange: (uploads: UploadedFile[]) => void;
}

export function SalesSection({
  data,
  onChange,
  questionnaireId,
  uploads,
  onUploadsChange,
}: SalesSectionProps) {
  const update = <K extends keyof SalesData>(field: K, value: SalesData[K]) => {
    onChange({ ...data, [field]: value });
  };

  const timelineStages = data.salesProcessTimeline.map((s) => s.stage);
  const handleTimelineChange = (stages: string[]) => {
    update(
      'salesProcessTimeline',
      stages.map((stage, i) => ({ stage, order: i + 1 })),
    );
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Who do you sell to?</label>
        <textarea
          value={data.targetBuyers}
          onChange={(e) => update('targetBuyers', e.target.value)}
          rows={3}
          placeholder="Describe your target buyers..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <RepeatableListInput
        label="Top 5 Buyer Job Titles"
        items={data.topBuyerTitles}
        onChange={(items) => update('topBuyerTitles', items)}
        maxItems={5}
        placeholder="e.g., VP of Engineering"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          How do you meet your buyers?
        </label>
        <textarea
          value={data.buyerChannels}
          onChange={(e) => update('buyerChannels', e.target.value)}
          rows={3}
          placeholder="Inbound, outbound, events, referrals..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <RepeatableListInput
        label="Sales Process Timeline (stages in order)"
        items={timelineStages}
        onChange={handleTimelineChange}
        maxItems={10}
        placeholder="e.g., Intro Call"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Typical Sales Cycle Length
        </label>
        <input
          type="text"
          value={data.salesCycle}
          onChange={(e) => update('salesCycle', e.target.value)}
          placeholder="e.g., 30-60 days"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <RepeatableListInput
        label="Major Buyer Objections"
        items={data.majorObjections}
        onChange={(items) => update('majorObjections', items)}
        maxItems={10}
        placeholder="e.g., Price is too high"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          How do you handle those objections?
        </label>
        <textarea
          value={data.objectionHandling}
          onChange={(e) => update('objectionHandling', e.target.value)}
          rows={4}
          placeholder="Describe your objection handling strategies..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      <div className="pt-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sales Materials (demo videos, transcripts, emails)
        </label>
        <FileUploadZone
          questionnaireId={questionnaireId}
          section="sales"
          uploads={uploads}
          onUploadsChange={onUploadsChange}
        />
      </div>
    </div>
  );
}
